import { useState, useMemo } from 'react';
import { useLeadSourceWithCampaigns } from '@/hooks/useLeadSourceWithCampaigns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown, ChevronRight, TrendingUp, TrendingDown, Download, Target, Users, CheckCircle, DollarSign, Layers, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricCard } from './MetricCard';
import { SourcePerformanceChart } from './SourcePerformanceChart';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface LeadSourceReportProps {
  startDate: Date;
  endDate: Date;
  timezone?: string;
  compareEnabled?: boolean;
}

export const LeadSourceReport = ({ startDate, endDate, timezone: propTimezone }: LeadSourceReportProps) => {
  const { timezone: contextTimezone, formatDate } = useTimezoneUtils();
  const timezone = propTimezone || contextTimezone;
  const { formatMoney } = useCompanyLocale();

  const { data: sources, isLoading } = useLeadSourceWithCampaigns(startDate, endDate, timezone);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' | null }>({
    key: null,
    direction: null
  });

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return { key: null, direction: null };
    });
  };

  const sortedSources = useMemo(() => {
    if (!sources || !sortConfig.key || !sortConfig.direction) return sources;

    return [...sources].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof typeof a];
      const bValue = b[sortConfig.key as keyof typeof b];

      if (aValue === bValue) return 0;

      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [sources, sortConfig]);

  const allIds = useMemo(() => new Set(sortedSources?.map(s => s.source_id) ?? []), [sortedSources]);
  const allSelected = allIds.size > 0 && allIds.size === selectedIds.size;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-3 w-3 ml-1 inline" />;
    if (sortConfig.direction === 'asc') return <ArrowUp className="h-3 w-3 ml-1 inline" />;
    return <ArrowDown className="h-3 w-3 ml-1 inline" />;
  };

  const escapeCSV = (val: string | number) => {
    const s = String(val);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const handleExportCSV = () => {
    if (!sortedSources) return;

    const toExport = selectedIds.size > 0
      ? sortedSources.filter(s => selectedIds.has(s.source_id))
      : sortedSources;

    const headers = [
      'Type', 'Name',
      'Leads', 'Booked', 'Quoted', 'Completed', 'Cancelled',
      'Conv %',
      'Ad Cost', 'Revenue', 'Profit',
      'CPA', 'ROAS',
    ];

    const rows: string[][] = [headers];

    toExport.forEach(source => {
      rows.push([
        'Source',
        source.source_name,
        source.total_leads.toString(),
        source.total_bookings.toString(),
        source.jobs_quoted.toString(),
        source.jobs_completed.toString(),
        source.jobs_cancelled.toString(),
        source.conversion_rate.toFixed(1) + '%',
        source.actual_cost.toFixed(2),
        source.total_revenue.toFixed(2),
        source.campaign_profit.toFixed(2),
        source.cost_per_acquisition ? source.cost_per_acquisition.toFixed(2) : '',
        source.return_on_ad_spend ? source.return_on_ad_spend.toFixed(2) : '',
      ]);

      source.campaigns.forEach(campaign => {
        rows.push([
          '  Campaign',
          '  ' + campaign.campaign_name,
          campaign.total_leads.toString(),
          campaign.total_bookings.toString(),
          campaign.jobs_quoted.toString(),
          campaign.jobs_completed.toString(),
          campaign.jobs_cancelled.toString(),
          campaign.conversion_rate.toFixed(1) + '%',
          campaign.actual_cost.toFixed(2),
          campaign.total_revenue.toFixed(2),
          campaign.campaign_profit.toFixed(2),
          campaign.cost_per_acquisition ? campaign.cost_per_acquisition.toFixed(2) : '',
          campaign.return_on_ad_spend ? campaign.return_on_ad_spend.toFixed(2) : '',
        ]);
      });
    });

    const csv = rows.map(row => row.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lead-sources-${formatDate(startDate, 'yyyy-MM-dd')}-${formatDate(endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!sortedSources || sortedSources.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-center">No lead source data available for this period.</p>
        </CardContent>
      </Card>
    );
  }

  const totalLeads = sortedSources.reduce((sum, s) => sum + s.total_leads, 0);
  const totalRevenue = sortedSources.reduce((sum, s) => sum + s.total_revenue, 0);
  const totalConverted = sortedSources.reduce((sum, s) => sum + s.leads_converted, 0);
  const overallConversionRate = totalLeads > 0 ? (totalConverted / totalLeads) * 100 : 0;

  const exportLabel = selectedIds.size > 0
    ? `Export ${selectedIds.size} Selected`
    : 'Export All';

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard
          title="Active Sources"
          value={sortedSources.filter(s => s.is_active).length}
          subtitle={`${sortedSources.reduce((sum, s) => sum + s.campaign_count, 0)} campaigns`}
          icon={Target}
        />
        <MetricCard
          title="Total Leads"
          value={totalLeads}
          subtitle="all sources"
          icon={Users}
        />
        <MetricCard
          title="Overall Conv. Rate"
          value={`${overallConversionRate.toFixed(1)}%`}
          subtitle={`${totalConverted} converted`}
          icon={CheckCircle}
        />
        <MetricCard
          title="Total Revenue"
          value={formatMoney(totalRevenue)}
          subtitle="from all sources"
          icon={DollarSign}
        />
      </div>

      {/* Performance Chart */}
      <SourcePerformanceChart
        data={sortedSources.map(s => ({
          source_name: s.source_name,
          total_leads: s.total_leads,
          leads_converted: s.leads_converted,
          total_revenue: s.total_revenue,
          conversion_rate: s.conversion_rate
        }))}
      />

      {/* Main Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Lead Source Performance</CardTitle>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            {exportLabel}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={someSelected ? 'indeterminate' : allSelected}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Source / Campaign</TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('conversion_rate')}
                  >
                    Conv. % {getSortIcon('conversion_rate')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('total_leads')}
                  >
                    Leads {getSortIcon('total_leads')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('total_bookings')}
                  >
                    Booked {getSortIcon('total_bookings')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('jobs_completed')}
                  >
                    Closed {getSortIcon('jobs_completed')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('actual_cost')}
                  >
                    Ad Cost {getSortIcon('actual_cost')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('total_revenue')}
                  >
                    Revenue {getSortIcon('total_revenue')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('campaign_profit')}
                  >
                    Profit {getSortIcon('campaign_profit')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('cost_per_acquisition')}
                  >
                    CPA {getSortIcon('cost_per_acquisition')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('return_on_ad_spend')}
                  >
                    ROAS {getSortIcon('return_on_ad_spend')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSources.map(source => (
                  <>
                    {/* Source Row */}
                    <TableRow
                      key={source.source_id}
                      className="font-semibold bg-muted/30"
                    >
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(source.source_id)}
                          onCheckedChange={() => toggleSelect(source.source_id)}
                          aria-label={`Select ${source.source_name}`}
                        />
                      </TableCell>
                      <TableCell
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setExpandedId(expandedId === source.source_id ? null : source.source_id)}
                      >
                        {expandedId === source.source_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </TableCell>
                      <TableCell
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setExpandedId(expandedId === source.source_id ? null : source.source_id)}
                      >
                        <div className="flex items-center gap-2">
                          <Layers className="h-4 w-4 text-primary" />
                          <span className="font-semibold">{source.source_name}</span>
                          <Badge variant="secondary" className="ml-2">
                            {source.campaign_count} {source.campaign_count === 1 ? 'campaign' : 'campaigns'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span className={source.conversion_rate >= 50 ? 'text-success font-bold' : source.conversion_rate >= 25 ? 'text-info font-semibold' : ''}>
                            {source.conversion_rate.toFixed(1)}%
                          </span>
                          {source.conversion_rate >= 50 ? <TrendingUp className="h-3 w-3 text-success" /> : source.conversion_rate < 10 ? <TrendingDown className="h-3 w-3 text-destructive" /> : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold">{source.total_leads}</TableCell>
                      <TableCell className="text-right font-semibold">{source.total_bookings}</TableCell>
                      <TableCell className="text-right">
                        <span className="text-success font-semibold">{source.jobs_completed}</span>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatMoney(source.actual_cost)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{formatMoney(source.total_revenue)}</TableCell>
                      <TableCell className="text-right font-semibold">
                        <span className={source.campaign_profit >= 0 ? 'text-success' : 'text-destructive'}>
                          {formatMoney(source.campaign_profit)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {source.cost_per_acquisition ? formatMoney(source.cost_per_acquisition) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {source.return_on_ad_spend ? (
                          <span className={source.return_on_ad_spend >= 2 ? 'text-success font-bold' : ''}>
                            {source.return_on_ad_spend.toFixed(2)}x
                          </span>
                        ) : '-'}
                      </TableCell>
                    </TableRow>

                    {/* Campaign Rows (when expanded) */}
                    {expandedId === source.source_id && source.campaigns.map(campaign => (
                      <TableRow key={campaign.campaign_id} className="bg-background">
                        <TableCell />
                        <TableCell />
                        <TableCell className="pl-12 text-muted-foreground">
                          {campaign.campaign_name}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {campaign.conversion_rate.toFixed(1)}%
                        </TableCell>
                        <TableCell className="text-right text-sm">{campaign.total_leads}</TableCell>
                        <TableCell className="text-right text-sm">{campaign.total_bookings}</TableCell>
                        <TableCell className="text-right text-sm text-success">
                          {campaign.jobs_completed}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {formatMoney(campaign.actual_cost)}
                        </TableCell>
                        <TableCell className="text-right text-sm">{formatMoney(campaign.total_revenue)}</TableCell>
                        <TableCell className="text-right text-sm">
                          <span className={campaign.campaign_profit >= 0 ? 'text-success' : 'text-destructive'}>
                            {formatMoney(campaign.campaign_profit)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {campaign.cost_per_acquisition ? formatMoney(campaign.cost_per_acquisition) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {campaign.return_on_ad_spend ? (
                            <span className={campaign.return_on_ad_spend >= 2 ? 'text-success font-semibold' : ''}>
                              {campaign.return_on_ad_spend.toFixed(2)}x
                            </span>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
